// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAdminToast } from './useAdminToast';
import type { EmailTemplate } from '../../types/admin';

interface AdminBulkEmailDialogProps {
  isOpen: boolean;
  selectedUserIds: string[];
  selectedUserEmails: string[];
  onClose: () => void;
  onSent?: (subject: string, sentCount: number, failedCount: number) => void | Promise<void>;
}

export const AdminBulkEmailDialog: React.FC<AdminBulkEmailDialogProps> = ({
  isOpen,
  selectedUserIds,
  selectedUserEmails,
  onClose,
  onSent,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isHtml, setIsHtml] = useState(true);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<{ success: number; failed: number; details?: any[] } | null>(null);
  const [aiGoal, setAiGoal] = useState('');
  const [aiTone, setAiTone] = useState('friendly');
  const [aiAudience, setAiAudience] = useState('');
  const [aiHighlights, setAiHighlights] = useState('');
  const [aiCtaText, setAiCtaText] = useState('Zum Dashboard');
  const [aiCtaUrl, setAiCtaUrl] = useState('https://lrdene.de/dashboard');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraftMeta, setAiDraftMeta] = useState<any | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const { showSuccess, showError } = useAdminToast();

  const aiSummary = useMemo(() => {
    if (!aiDraftMeta) return null;
    return [
      aiDraftMeta.provider ? `Provider: ${aiDraftMeta.provider}` : null,
      aiDraftMeta.previewText ? `Preview: ${aiDraftMeta.previewText}` : null,
      aiDraftMeta.cta?.text ? `CTA: ${aiDraftMeta.cta.text}` : null,
    ].filter(Boolean).join(' • ');
  }, [aiDraftMeta]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    } else {
      setSelectedTemplateId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name, subject, preview_text, html_body, text_body, updated_at')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Error loading email templates:', err);
      showError(err.message || 'Failed to load templates');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiGoal.trim() && !aiHighlights.trim()) {
      showError('Add a campaign goal or highlight for the AI assistant.');
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-marketing-email', {
        body: {
          subject: subject || undefined,
          campaignGoal: aiGoal || undefined,
          highlights: aiHighlights || undefined,
          audience: aiAudience || undefined,
          tone: aiTone,
          callToActionText: aiCtaText || undefined,
          callToActionUrl: aiCtaUrl || undefined,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.subject && !subject) {
        setSubject(data.subject);
      }
      if (data?.html) {
        setBody(data.html);
        setIsHtml(true);
      } else if (data?.text) {
        setBody(data.text);
        setIsHtml(false);
      }

      setAiDraftMeta(data);
      showSuccess('AI draft generated. Review and adjust before sending.');
    } catch (err: any) {
      console.error('AI generation failed:', err);
      showError(err.message || 'Failed to generate email with AI');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleApplyTemplate = async (templateId: string) => {
    const template = templates.find((tpl) => tpl.id === templateId);
    if (!template) return;

    setSubject(template.subject || '');
    if (template.html_body) {
      setBody(template.html_body);
      setIsHtml(true);
    } else {
      setBody(template.text_body || '');
      setIsHtml(false);
    }
    setSelectedTemplateId(templateId);
    showSuccess('Template applied to composer');

    try {
      const now = new Date().toISOString();
      await supabase
        .from('email_templates')
        .update({ last_used_at: now })
        .eq('id', templateId);
      setTemplates((prev) =>
        prev.map((tpl) => (tpl.id === templateId ? { ...tpl, last_used_at: now } : tpl))
      );
    } catch (err) {
      console.error('Failed to update template usage timestamp:', err);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      showError('Subject and body are required');
      return;
    }

    if (selectedUserIds.length === 0) {
      showError('No users selected');
      return;
    }

    setSending(true);
    setResults(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the Edge Function (emails will be fetched server-side)
      const { data, error } = await supabase.functions.invoke('send-bulk-email', {
        body: {
          user_ids: selectedUserIds,
          subject: subject.trim(),
          body: body.trim(),
          is_html: isHtml,
        },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setResults({
        success: data.sent || 0,
        failed: data.failed || 0,
        details: data.results || [],
      });

      if (data.sent > 0) {
        showSuccess(`Successfully sent ${data.sent} email(s)`);
        if (data.failed > 0) {
          showError(`${data.failed} email(s) failed to send`);
        }
        await onSent?.(subject.trim(), data.sent || 0, data.failed || 0);
      } else {
        showError('Failed to send all emails');
        await onSent?.(subject.trim(), 0, data.failed || selectedUserIds.length);
      }
    } catch (err: any) {
      console.error('Error sending bulk email:', err);
      showError(err.message || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setBody('');
    setIsHtml(true);
    setResults(null);
    setAiGoal('');
    setAiTone('friendly');
    setAiAudience('');
    setAiHighlights('');
    setAiCtaText('Zum Dashboard');
    setAiCtaUrl('https://lrdene.de/dashboard');
    setAiDraftMeta(null);
    setSelectedTemplateId('');
    onClose();
  };

  return (
    <button
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-email-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 id="bulk-email-title" className="text-lg sm:text-xl font-bold text-luna-text-light dark:text-luna-text-dark pr-2">
            Send Bulk Email
          </h3>
          <button
            onClick={handleClose}
            className="text-luna-text-light/50 dark:text-luna-text-dark/50 hover:text-luna-text-light dark:hover:text-luna-text-dark p-2 -mt-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Close dialog"
            disabled={sending}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="space-y-4">
          {/* Recipients Info */}
          <div className="bg-luna-secondary/5 p-3 rounded-lg">
            <p className="text-sm text-luna-text-light dark:text-luna-text-dark">
              <strong>Recipients:</strong> {selectedUserIds.length} user(s)
            </p>
            {selectedUserEmails.length > 0 && selectedUserEmails.length <= 5 && (
              <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                {selectedUserEmails.join(', ')}
              </p>
            )}
            {selectedUserEmails.length > 5 && (
              <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                {selectedUserEmails.slice(0, 5).join(', ')} and {selectedUserEmails.length - 5} more...
              </p>
            )}
          </div>

          {/* Saved Templates */}
          {templates.length > 0 && (
            <div className="bg-luna-secondary/10 p-3 rounded-lg space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                    Saved Templates
                  </p>
                  <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                    Apply a reusable campaign to prefill the subject and message.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fetchTemplates}
                  disabled={templateLoading}
                  className="px-3 py-1.5 rounded-lg border border-luna-secondary/30 text-xs font-medium hover:bg-luna-secondary/20 disabled:opacity-60"
                >
                  {templateLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  id="template-select"
                  value={selectedTemplateId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedTemplateId(value);
                    if (value) {
                      handleApplyTemplate(value);
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
                >
                  <option value="">Select template…</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} — {template.subject}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <label htmlFor="email-subject" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
              Subject *
            </label>
            <input
              id="email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] touch-manipulation"
              disabled={sending}
              aria-label="Email subject"
            />
          </div>

          {/* Body */}
          <div>
            <label htmlFor="email-body" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
              Message *
            </label>
            <textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email message body"
              rows={10}
              className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark resize-y min-h-[120px] touch-manipulation"
              disabled={sending}
              aria-label="Email message body"
            />
          </div>

          {/* AI Assistant */}
          <div className="border border-dashed border-luna-secondary/50 rounded-xl p-4 space-y-3 bg-luna-secondary/5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">AI Campaign Assistant</p>
                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                  Describe what you want to announce—AI drafts the email and imports it above.
                </p>
              </div>
              {aiSummary && (
                <span className="text-[11px] text-luna-text-light/60 dark:text-luna-text-dark/60">{aiSummary}</span>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label htmlFor="ai-tone" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">Tone</label>
                <select
                  id="ai-tone"
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                  disabled={aiGenerating}
                  className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
                >
                  <option value="friendly">Friendly</option>
                  <option value="professional">Professional</option>
                  <option value="inspirational">Inspirational</option>
                  <option value="bold">Bold & Confident</option>
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="ai-audience" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">Audience segment</label>
                <input
                  id="ai-audience"
                  value={aiAudience}
                  onChange={(e) => setAiAudience(e.target.value)}
                  disabled={aiGenerating}
                  placeholder="e.g. Zivilrecht-Abos, Anwälte, Trial users"
                  className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="ai-goal" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">Campaign goal *</label>
              <textarea
                id="ai-goal"
                value={aiGoal}
                onChange={(e) => setAiGoal(e.target.value)}
                disabled={aiGenerating}
                rows={2}
                placeholder="Describe what you want the email to achieve."
                className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="ai-highlights" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">Talking points / highlights</label>
              <textarea
                id="ai-highlights"
                value={aiHighlights}
                onChange={(e) => setAiHighlights(e.target.value)}
                disabled={aiGenerating}
                rows={2}
                placeholder="Key product updates, offer details, statistics …"
                className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label htmlFor="ai-cta-text" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">CTA text</label>
                <input
                  id="ai-cta-text"
                  value={aiCtaText}
                  onChange={(e) => setAiCtaText(e.target.value)}
                  disabled={aiGenerating}
                  className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="ai-cta-url" className="text-xs uppercase tracking-wide text-luna-text-light/70 dark:text-luna-text-dark/70">CTA URL</label>
                <input
                  id="ai-cta-url"
                  value={aiCtaUrl}
                  onChange={(e) => setAiCtaUrl(e.target.value)}
                  disabled={aiGenerating}
                  className="w-full px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={handleGenerateWithAI}
                disabled={aiGenerating}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-luna-secondary text-luna-primary font-bold hover:opacity-90 disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-base">auto_awesome</span>
                {aiGenerating ? 'Generating…' : 'Generate with AI'}
              </button>
              {aiDraftMeta?.fallback && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">AI service unavailable—using fallback copy.</p>
              )}
            </div>
          </div>

          {/* HTML Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-html"
              checked={isHtml}
              onChange={(e) => setIsHtml(e.target.checked)}
              disabled={sending}
              className="rounded border-luna-secondary/30"
              aria-label="Send as HTML email"
            />
            <label htmlFor="is-html" className="text-sm text-luna-text-light dark:text-luna-text-dark">
              Send as HTML email
            </label>
          </div>

          {/* Results */}
          {results && (
            <div className={`p-3 rounded-lg ${results.failed > 0 ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
              <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                Sent: {results.success} | Failed: {results.failed}
              </p>
              {results.details && results.details.some((r: any) => !r.success) && (
                <details className="mt-2">
                  <summary className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 cursor-pointer">
                    View failed emails
                  </summary>
                  <ul className="mt-2 text-xs text-red-600 dark:text-red-400 space-y-1">
                    {results.details.filter((r: any) => !r.success).map((r: any, idx: number) => (
                      <li key={idx}>{r.email}: {r.error || 'Unknown error'}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-luna-secondary/10">
            <button
              onClick={handleClose}
              className="w-full sm:w-auto px-6 py-3 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-gray-100 dark:hover:bg-luna-secondary/10 transition-colors min-h-[44px] touch-manipulation"
              disabled={sending}
            >
              {results ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
              className="w-full sm:w-auto px-6 py-3 rounded-lg bg-luna-secondary text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
              aria-label="Send bulk email"
            >
              {sending ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Sending...
                </span>
              ) : (
                `Send to ${selectedUserIds.length} user(s)`
              )}
            </button>
          </div>
        </div>
      </div>
    </button>
  );
};
